
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// Fetch all candidates for a specific job
export const fetchCandidatesForJob = async (jobId: string) => {
  try {
    // Fetch matches with candidates for this job
    const { data, error } = await supabase
      .from('matches')
      .select(`
        id,
        candidate_id,
        job_id,
        score,
        status,
        created_at,
        candidates(
          id,
          name,
          email,
          skills
        )
      `)
      .eq('job_id', jobId);
    
    if (error) {
      throw error;
    }
    
    // Transform the data for kanban display
    return data?.map(match => ({
      id: match.id,
      candidateId: match.candidate_id,
      jobId: match.job_id,
      stage: match.status || 'matched', // Default to matched if no status
      name: match.candidates?.name || 'Unknown',
      email: match.candidates?.email || '',
      skills: match.candidates?.skills || [],
      score: match.score || 0,
      createdAt: match.created_at
    })) || [];
    
  } catch (error) {
    console.error('Error fetching kanban data:', error);
    toast({
      title: "Error",
      description: "Failed to load recruitment process data",
      variant: "destructive",
    });
    return [];
  }
};

// Update a candidate's stage in the recruitment process
export const updateCandidateStage = async (matchId: string, newStage: string) => {
  try {
    // Update the match status in the database
    const { error } = await supabase
      .from('matches')
      .update({ status: newStage })
      .eq('id', matchId);
    
    if (error) throw error;
    
    toast({
      title: "Success",
      description: `Candidate moved to ${newStage} stage`,
    });
    
    return true;
  } catch (error) {
    console.error('Error updating candidate stage:', error);
    toast({
      title: "Error",
      description: "Failed to update candidate stage",
      variant: "destructive",
    });
    return false;
  }
};

// Add a candidate to a job
export const addCandidateToJob = async (jobId: string, candidateId: string) => {
  try {
    // Check if the candidate already exists in this job
    const { data: existingMatch } = await supabase
      .from('matches')
      .select('id')
      .eq('job_id', jobId)
      .eq('candidate_id', candidateId)
      .single();
    
    if (existingMatch) {
      toast({
        title: "Warning",
        description: "This candidate is already in the recruitment process",
      });
      return false;
    }
    
    // Add candidate to job with initial stage "applied"
    const { error } = await supabase
      .from('matches')
      .insert({
        job_id: jobId,
        candidate_id: candidateId,
        score: 0, // Initial score, will be calculated later
        status: 'matched'
      });
    
    if (error) throw error;
    
    toast({
      title: "Success",
      description: "Candidate added to recruitment process",
    });
    
    return true;
  } catch (error) {
    console.error('Error adding candidate to job:', error);
    toast({
      title: "Error",
      description: "Failed to add candidate to recruitment process",
      variant: "destructive",
    });
    return false;
  }
};
